export interface PostMessageData {
    action: string;
    [key: string]: any;
}

export interface PostMessageListener {
    id: string;
    action: string;
    callback: (data: PostMessageData) => void;
}

export const postMessageAction = {
    OPEN_NAVIGATION_MODAL: 'openNavigationModal',
    OPEN_IN_NEW_TAB: 'openInNewTab',
    OPEN_HELP_CENTER: 'openHelpCenter',
    OPEN_INSTANCE_EXPLORER_MODAL: 'openInstanceExplorerModal',
    REAUTHENTICATE_AND_REDIRECT: 'reauthenicateAndRedirect',
    BREADCRUMB_CHANGED: 'onBreadcrumbChanged',
};

class PostMessageService {
    private listeners: Map<string, PostMessageListener> = new Map();

    private isListening = false;

    constructor() {
        this.startListening();
    }

    getTargetOrigin = () => {
        const currentOrigin = window.location.origin;
        const parentOrigin = currentOrigin.replace(`://edc.`, '://');
        return parentOrigin;
    };

    /**
     * Send a message to the parent window
     */
    sendToParent = (data: PostMessageData): void => {
        try {
            const targetOrigin = this.getTargetOrigin();
            window.parent.postMessage(data, targetOrigin);
        } catch (error) {
            console.error('Error sending postMessage:', error);
        }
    };

    /**
     * Send a message to a specific window
     */
    static sendToWindow(targetWindow: Window, data: PostMessageData, targetOrigin = '*'): void {
        try {
            targetWindow.postMessage(data, targetOrigin);
        } catch (error) {
            console.error('Error sending postMessage to window:', error);
        }
    }

    /**
     * Add a listener for specific action
     */
    addListener(id: string, action: string, callback: (data: PostMessageData) => void): void {
        const listener: PostMessageListener = {
            id,
            action,
            callback,
        };

        this.listeners.set(id, listener);
    }

    /**
     * Remove a listener by ID
     */
    removeListener(id: string): void {
        if (this.listeners.has(id)) {
            this.listeners.delete(id);
        }
    }

    /**
     * Remove all listeners for a specific action
     */
    removeListenersByAction(action: string): void {
        const toRemove: string[] = [];

        this.listeners.forEach((listener, id) => {
            if (listener.action === action) {
                toRemove.push(id);
            }
        });

        toRemove.forEach((id) => this.listeners.delete(id));
    }

    /**
     * Get all active listeners
     */
    getActiveListeners(): PostMessageListener[] {
        return Array.from(this.listeners.values());
    }

    /**
     * Start listening for messages
     */
    private startListening(): void {
        if (this.isListening) return;

        window.addEventListener('message', this.handleMessage);
        this.isListening = true;
    }

    /**
     * Stop listening for messages
     */
    stopListening(): void {
        if (!this.isListening) return;

        window.removeEventListener('message', this.handleMessage);
        this.isListening = false;
    }

    /**
     * Handle incoming messages
     */
    private handleMessage = (event: MessageEvent): void => {
        try {
            const data = event.data as PostMessageData;

            if (!data || !data.action) {
                return; // Ignore messages without action
            }

            // Call all listeners that match the action
            this.listeners.forEach((listener) => {
                if (listener.action === data.action) {
                    try {
                        listener.callback(data);
                    } catch (error) {
                        console.error(`Error in postMessage listener ${listener.id}:`, error);
                    }
                }
            });
        } catch (error) {
            console.error('Error handling postMessage:', error);
        }
    };

    /**
     * Clean up all listeners and stop listening
     */
    destroy(): void {
        this.listeners.clear();
        this.stopListening();
    }
}

// Create a singleton instance
export const postMessageService = new PostMessageService();

// Export the class for testing or creating custom instances
export default PostMessageService;
